/**
 * Dynamic-generation create path (M12, FR-8.5/1.3; #150) — the default (and, in the
 * UI, only) way to create a game. The host has nothing to choose up front: they just
 * create a pack-less game (`mode: 'DYNAMIC'`), born in `LOBBY`; players then supply
 * category wishes and the host starts generation from the live lobby.
 *
 * Since uploaded packs are no longer a public option (#150), this is presented as a
 * single, unadorned **"Create Game"** button — the create screen's heading already
 * explains what a dynamic game is, so no per-lobby copy is needed. On success it mints
 * a host ticket, persists the session (so a reload restores it), and hands both back to
 * the parent via `onGameReady`.
 */

import { useCallback, useState } from 'react';
import type { HostMode } from '@jeopardy/shared';
import { createGame, requestHostTicketForGame, type HouseRules } from '../net/pack-api';
import { saveHostSession } from '../net/host-session';
import { CreateCostHint } from './CreateCostHint';

interface DynamicLobbyProps {
  /** Called once the dynamic game is created: host ticket + the players' join code. */
  onGameReady: (ticket: string, joinCode: string) => void;
  /** Who runs the game (#91). Defaults to `HUMAN`. */
  hostMode?: HostMode;
  /**
   * Whether the host may create a game (#171 review, PR #179). `false` when the host is
   * out of credits for this paid dynamic game: the "Create Game" button is not rendered
   * at all — the host tops up via the credit slot above first. Defaults to `true`.
   */
  canCreate?: boolean;
  /**
   * The host's current credit balance (#188), threaded from `CreateGame`. When set and
   * `>= 1` a cost hint renders above the button; `undefined` (free game / no identity) → no hint.
   */
  availableCredits?: number;
  /**
   * House Rules (#232), threaded from `CreateGame`. The resolved, bounded per-game
   * options (power-ups / buzzer / scoring); `undefined` = Classic (default), so the
   * create body is unchanged.
   */
  houseRules?: HouseRules;
}

export function DynamicLobby({
  onGameReady,
  hostMode = 'HUMAN',
  canCreate = true,
  availableCredits,
  houseRules,
}: DynamicLobbyProps): React.JSX.Element | null {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleCreate = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const { gameId, joinCode } = await createGame(
        undefined,
        'DYNAMIC',
        hostMode,
        houseRules,
      );
      const ticket = await requestHostTicketForGame(gameId);
      saveHostSession({ gameId, joinCode });
      onGameReady(ticket, joinCode);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the game');
    } finally {
      setBusy(false);
    }
  }, [onGameReady, hostMode, houseRules]);

  // Out of credits (#171 review, PR #179): the create button is simply withdrawn — the host
  // tops up via the credit balance + get-more-credits slot that sits directly above. With no
  // button and no error there is nothing to show, so render NOTHING: a bare `.dynamic-lobby`
  // would still paint its padded, bordered white panel — the "empty white box" under the
  // "Redeem a code" button (PR #179 review). `error` can only be set by a create attempt,
  // which needs the button, so it is always null here — the guard is just defensive.
  if (!canCreate && !error) return null;

  return (
    <div className="dynamic-lobby" data-testid="dynamic-lobby">
      {error && (
        <div className="error-text" data-testid="dynamic-lobby-error">
          {error}
        </div>
      )}
      {canCreate && (
        <>
          <CreateCostHint
            availableCredits={availableCredits}
            className="dynamic-lobby__cost"
          />
          <button
            type="button"
            className="btn btn--primary"
            data-testid="create-dynamic-game"
            disabled={busy}
            onClick={() => void handleCreate()}
          >
            {busy ? 'Working…' : 'Create Game'}
          </button>
        </>
      )}
    </div>
  );
}
